import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, CheckCircle2, LibraryBig, X } from "lucide-react";
import {
  listMyOrganisations, listMyQuizPublications,
  publishQuizToOrganisation, unpublishQuizFromOrganisation,
} from "./utils/organisationApi";
import "./OrganisationLibrary.css";

export default function OrganisationPublishDialog({ quiz, onClose }) {
  const navigate = useNavigate();
  const [organisations, setOrganisations] = useState([]);
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busyId, setBusyId] = useState("");
  const [confirmId, setConfirmId] = useState("");
  const busyRef = useRef(false);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      setLoading(true);
      try {
        const [mine, published] = await Promise.all([listMyOrganisations(), listMyQuizPublications()]);
        if (!alive) return;
        setOrganisations(Array.isArray(mine.organisations) ? mine.organisations : []);
        setPublications(Array.isArray(published.publications) ? published.publications : []);
        setError("");
      } catch (loadError) {
        if (alive) setError(loadError.message || "Could not load your organisations.");
      } finally { if (alive) setLoading(false); }
    };
    load();
    return () => { alive = false; };
  }, []);

  const selectedPublications = publications.filter((entry) => entry.quizId === quiz.id);
  const options = organisations;

  const update = async (org, isPublished) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusyId(org.organisationId);
    setError("");
    setNotice("");
    try {
      if (isPublished) await unpublishQuizFromOrganisation(org.organisationId, quiz.id);
      else await publishQuizToOrganisation(org.organisationId, quiz.id);
      setPublications((current) => isPublished
        ? current.filter((item) => !(item.organisationId === org.organisationId && item.quizId === quiz.id))
        : [...current, { organisationId: org.organisationId, organisationName: org.name, quizId: quiz.id }]);
      setConfirmId("");
      setNotice(isPublished ? `Removed from ${org.name}'s Shared Library.` : `Published to ${org.name}'s Shared Library.`);
      try {
        const fresh = await listMyQuizPublications();
        setPublications(Array.isArray(fresh.publications) ? fresh.publications : []);
      } catch (_refreshError) {
        setError("The change was saved, but the publication list could not refresh. Reopen this dialog to check its latest status.");
      }
    } catch (actionError) {
      setError(actionError.message || "Could not update this publication.");
    } finally {
      busyRef.current = false;
      setBusyId("");
    }
  };

  return (
    <div className="ql-modal-backdrop" onMouseDown={(event) => {
      if (event.target === event.currentTarget && !busyId) onClose();
    }}>
      <div className="ql-modal" role="dialog" aria-modal="true" aria-labelledby="ql-publish-title">
        <div className="ql-modal-head"><span className="ql-modal-symbol"><LibraryBig size={23} /></span>
          <button type="button" onClick={onClose} disabled={Boolean(busyId)} aria-label="Close publication dialog"><X size={20} /></button></div>
        <h2 id="ql-publish-title">Share Test to Organisation</h2>
        <p>Publish <strong>{quiz.quizTitle || quiz.title || quiz.quizCode}</strong> to a Shared Library. Your original test stays in My Tests and only you can change this publication.</p>
        {error && <div className="ql-feedback error" role="alert">{error}</div>}
        {notice && <div className="ql-feedback success" role="status"><CheckCircle2 size={17} />{notice}</div>}
        {loading ? <p className="ql-muted">Loading organisations…</p> : options.length ? (
          <div className="ql-publish-list">{options.map((org) => {
            const publication = selectedPublications.find((item) => item.organisationId === org.organisationId);
            const published = Boolean(publication);
            return <div className="ql-publish-row" key={org.organisationId}>
              <span className="ql-publish-icon"><Building2 size={18} /></span>
              <div><strong>{org.name}</strong><small>{published ? "Published in Shared Library" : "Not published"}</small></div>
              {published ? (confirmId === org.organisationId ? <div className="ql-publish-actions">
                <button type="button" className="ql-button quiet" onClick={() => setConfirmId("")} disabled={Boolean(busyId)}>Cancel</button>
                <button type="button" className="ql-button danger" onClick={() => update(org, true)} disabled={Boolean(busyId)}>{busyId === org.organisationId ? "Removing…" : "Confirm remove"}</button>
              </div> : <button type="button" className="ql-button quiet" onClick={() => setConfirmId(org.organisationId)} disabled={Boolean(busyId)}>Unpublish</button>) :
                <button type="button" className="ql-button primary" onClick={() => update(org, false)} disabled={Boolean(busyId)}>{busyId === org.organisationId ? "Publishing…" : "Publish"}</button>}
            </div>;
          })}</div>
        ) : <div className="ql-modal-empty"><Building2 size={27} /><strong>No organisations yet</strong><p>Create or join an organisation first, then return to share this test.</p>
          <button type="button" className="ql-button primary" onClick={() => navigate("/organisations")}>Go to Organisations</button></div>}
      </div>
    </div>
  );
}
